/**
 * Marks and lockups.
 *
 * ### The fallback is neutral, and that matters
 *
 * `Mark` and `Logo` used to render CQT Ammo's eagle PNG unconditionally. Since a
 * company only supplies `logoUrl` if someone sets one, that meant EVERY company
 * without a logo — and the platform-wide admin panel, which belongs to no
 * company at all — displayed one customer's mark. The same
 * vendor/customer confusion `docs/CONFIGURABLE-TOOLS.md` forbids, hiding in a
 * default.
 *
 * So the fallback is now drawn rather than imported: the company's initials on
 * its own accent colour. It needs no asset, works on any ground, and reads as
 * intentional rather than missing. CQT gets its eagle back by setting `logoUrl`
 * on its own row, like any other company would.
 *
 * The eagle asset is still shipped for the `cqt` brand entry point; it is simply
 * no longer what an unbranded company gets.
 */

/** Up to two initials from a company name. "Clara Biofuels" -> "CB". */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "•";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

/**
 * Small header mark: the company's logo, or its initials on the accent colour.
 *
 * Inline SVG rather than a data URI or a canvas: it inherits `--accent` and
 * `--plate-text` from the theme, so it recolours with the company's branding and
 * in dark mode without a second asset.
 */
export function Mark({
  className = "appbar__mark",
  wordmark,
  logoUrl,
}: {
  className?: string;
  wordmark?: string;
  logoUrl?: string;
}) {
  if (logoUrl) {
    return <img src={logoUrl} alt="" className={className} width={32} height={32} />;
  }
  return (
    <svg
      className={className}
      viewBox="0 0 32 32"
      width={32}
      height={32}
      role="presentation"
      aria-hidden="true"
    >
      <rect width="32" height="32" rx="8" fill="var(--accent)" />
      <text
        x="16"
        y="16"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="13"
        fontWeight="800"
        fontFamily="var(--font-display, system-ui)"
        fill="var(--accent-ink, #fff)"
      >
        {initials(wordmark || "Platform")}
      </text>
    </svg>
  );
}

/**
 * Large logo for the login / splash screen.
 *
 * Same reasoning as `Mark`. The sign-in screen is the FIRST thing a visitor
 * sees, so showing another company's logo there is the most visible possible
 * version of this mistake.
 */
export function Logo({
  size = 160,
  wordmark,
  logoUrl,
}: {
  size?: number;
  wordmark?: string;
  logoUrl?: string;
}) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt=""
        width={size}
        height={size}
        style={{ display: "block", width: size, height: size, objectFit: "contain" }}
      />
    );
  }
  return (
    <svg
      viewBox="0 0 160 160"
      width={size}
      height={size}
      role="presentation"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <rect width="160" height="160" rx="36" fill="var(--accent)" />
      <text
        x="80"
        y="82"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="62"
        fontWeight="800"
        fontFamily="var(--font-display, system-ui)"
        fill="var(--accent-ink, #fff)"
      >
        {initials(wordmark || "Platform")}
      </text>
    </svg>
  );
}

/**
 * Header lockup: mark + wordmark + the current app's name.
 * `app` is the short app label, e.g. "CRM" or "ADMIN".
 */
/**
 * The brand lockup: mark, wordmark, and which app this is.
 *
 * `wordmark` and `logoUrl` come from the company's own configuration. They used
 * to be one customer's name compiled in here, which meant every company —
 * and the platform-wide admin panel — was branded as CQT Ammo.
 *
 * Both fall back to the built-in mark and the product name, so a company with no
 * branding is unremarkable rather than blank.
 */
export function BrandLockup({
  app,
  wordmark,
  logoUrl,
}: {
  app?: string;
  wordmark?: string;
  logoUrl?: string;
}) {
  return (
    <div className="appbar__brand">
      <Mark wordmark={wordmark} logoUrl={logoUrl} />
      <span className="appbar__name">{wordmark || "Platform"}</span>
      {app ? <span className="appbar__app">{app}</span> : null}
    </div>
  );
}
